import { IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, PlayArrow } from '@mui/icons-material';
import { styled, Theme } from '@mui/material/styles';
import React from 'react';
import { useResolveAsset } from '../context';
import Lightbox, { MediaItem, isVideo } from './Lightbox';

interface GalleryProps {
  /** Images and/or videos. Type is inferred from the file extension. */
  images: MediaItem[];
}

// position: relative so the overlaid arrow buttons anchor to the strip.
const Frame = styled('div')(({ theme }) => ({
  position: 'relative',
  margin: theme.spacing(3, 0),
  clear: 'both', // never overlap a floated <Figure> from the paragraph above
}));

// Horizontal scroller with the native scrollbar suppressed — navigation is the
// overlaid arrow buttons, not a scrollbar. Scroll-snap keeps items aligned.
const Strip = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': { display: 'none' }, // WebKit
  borderRadius: theme.shape.borderRadius,
}));

// Shared filmstrip cell sizing (fixed height, natural width, snap + hover).
const cell = (theme: Theme) => ({
  height: 200,
  flex: '0 0 auto',
  borderRadius: theme.shape.borderRadius,
  scrollSnapAlign: 'start',
  cursor: 'zoom-in',
  transition: theme.transitions.create(['transform', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] },
  [theme.breakpoints.up('sm')]: { height: 260 },
  [theme.breakpoints.up('md')]: { height: 300 },
});

const Thumb = styled('img')(({ theme }) => ({
  display: 'block',
  width: 'auto',
  objectFit: 'cover',
  ...cell(theme),
}));

// aspect-ratio + fixed height gives videos a stable width before metadata loads.
const VideoCell = styled('div')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  aspectRatio: '16 / 9',
  ...cell(theme),
  '& video': { display: 'block', height: '100%', width: '100%', objectFit: 'cover' },
}));

const PlayBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  color: theme.palette.common.white,
  '& svg': { fontSize: 56, opacity: 0.9, filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.6))' },
}));

// Circular, semi-opaque overlay button vertically centered on the strip.
// `$side`/`$hidden` are transient (not forwarded to the DOM).
const ArrowButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== '$side' && prop !== '$hidden',
})<{ $side: 'left' | 'right'; $hidden: boolean }>(({ theme, $side, $hidden }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [$side]: theme.spacing(1),
  zIndex: 2,
  color: theme.palette.common.white,
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  backdropFilter: 'blur(2px)',
  opacity: $hidden ? 0 : 1,
  pointerEvents: $hidden ? 'none' : 'auto',
  transition: theme.transitions.create('opacity'),
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
}));

/**
 * Horizontally-scrolling media strip with overlaid prev/next buttons and a
 * click-to-open lightbox (prev/next, caption, counter, keyboard + Escape).
 * Items may be images or videos; author-relative `src`s resolve against the
 * blog's asset base.
 *
 *   <Gallery images={[{ src: 'images/a.jpg', alt: '…' }, { src: 'videos/b.mp4' }]} />
 */
const Gallery: React.FC<GalleryProps> = ({ images }) => {
  const resolveAsset = useResolveAsset();
  const stripRef = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);
  const [open, setOpen] = React.useState<number | null>(null);

  const updateArrows = React.useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  React.useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);

  const scrollByPage = (dir: -1 | 1) => {
    const el = stripRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  if (!images.length) return null;

  return (
    <Frame>
      <ArrowButton
        $side="left"
        $hidden={!canLeft}
        aria-label="Scroll left"
        onClick={() => scrollByPage(-1)}
      >
        <ChevronLeft />
      </ArrowButton>

      <Strip ref={stripRef} onScroll={updateArrows}>
        {images.map((img, i) =>
          isVideo(img) ? (
            <VideoCell key={img.src} onClick={() => setOpen(i)}>
              {/* #t=0.1 nudges the browser to paint the first frame as a poster */}
              <video
                src={`${resolveAsset(img.src)}#t=0.1`}
                muted
                playsInline
                preload="metadata"
                onLoadedMetadata={updateArrows}
              />
              <PlayBadge>
                <PlayArrow />
              </PlayBadge>
            </VideoCell>
          ) : (
            <Thumb
              key={img.src}
              src={resolveAsset(img.src)}
              alt={img.alt ?? ''}
              loading="lazy"
              onLoad={updateArrows}
              onClick={() => setOpen(i)}
            />
          ),
        )}
      </Strip>

      <ArrowButton
        $side="right"
        $hidden={!canRight}
        aria-label="Scroll right"
        onClick={() => scrollByPage(1)}
      >
        <ChevronRight />
      </ArrowButton>

      <Lightbox items={images} index={open} onIndex={setOpen} />
    </Frame>
  );
};

export default Gallery;
